import store from '@/store';
import { computed } from 'vue';
import { ErrorResponseData, NewUser, ServiceNumbers } from '@/types';
import { isAxiosError } from 'axios';

const users = computed<NewUser[]>(() => {
  return store.getters['data/pharmUsers']
})

export const globalScrollOptions = { wheelPropagation: false, minScrollbarLength: 60 }
export const globalScrollDistance = 300
export const DEFAULT_TAKE = 20

// Очистка null из строки
export const clearNull = (inputString: string | undefined) => {
  if (!inputString) return ''
  return inputString.replace(/\bnull\b|\bundefined\b/g, '');
}

// Преобразование ФИО в сокращенный вид
export const formatName = (fullName: string | undefined) => {
  if (!fullName) {
    return false;
  }
  const newName = clearNull(fullName);

  const nameParts = newName.trim().split(/\s+/);

  // Если в массиве меньше 2 слов, возвращаем оригинальную строку
  if (nameParts.length < 2) {
    return fullName;
  }

  const lastName = nameParts[0];
  const initials:string[] = [];

  // Проходим по оставшимся словам, берем первую букву и добавляем в массив инициалов
  for (let i = 1; i < nameParts.length; i++) {
    initials.push(nameParts[i][0] + '.');
  }

  // Объединяем фамилию и инициалы в одну строку
  return `${lastName} ${initials.join('')}`;
}

export const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(navigator.userAgent);

// Преобразование первой буквы в заглавную
export const makeFirstLetterUpper = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// Преобразование строки с ФИО в сокращение по первым буквам
export const formatNameInitials = (str: string) => {
  if (!str.trim()) return ""; // Обработка пустой строки или строки с пробелами

  const words = str.trim().split(/\s+/);

  // Возвращаем первые буквы из двух слов
  return words
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
};

// Проверка на несохраненные изменения на графике
export const checkUnsavedChanges = () => {
  // Ставим статус если есть новые внешние пользователи без смен
  store.commit('setUnsavedChanges', !!users.value.find(el => el.newUser))
}

// Получение текста ошибки в блоке catch у Axios
export const getAuthErrorMessage = (e: unknown): string => {
  if (isAxiosError<ErrorResponseData>(e)) {
    if (e.code === "ERR_NETWORK") {
      return "Отсутствует соединение с сервером";
    }
    // Выводим текст ошибки с сервера или заглушку
    return e.response?.data?.message ?? `Ошибка авторизации, ${e.message}`;
  }
  console.error("Неизвестная ошибка:", e);
  return "Неизвестная ошибка авторизации";
}

// Сортировка по serviceNumbers
export const sortServiceNumbersTypes = ["Основное место работы", "Внутреннее совместительство", "Внешнее совместительство", "Подработка"];

export const sortServiceNumbers = (serviceNumbers: ServiceNumbers[]) => {
  return serviceNumbers.sort((a, b) => {
    const typeEmploymentComparison = sortServiceNumbersTypes.indexOf(a.typeEmployment) - sortServiceNumbersTypes.indexOf(b.typeEmployment);
    if (typeEmploymentComparison !== 0) {
      return typeEmploymentComparison;
    }
    return b.bid - a.bid;
  });
}

// Форматирование номера телефона
export const formatPhoneNumber = (phoneNumber: string) => {
  // Удаляем все пробелы, дефисы и другие символы, оставляем только цифры и +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  // Если уже начинается с +, возвращаем как есть
  if (cleaned.startsWith('+')) return cleaned;

  // Если начинается с цифры, то добавляем +
  if (/^\d+$/.test(cleaned)) return `+${cleaned}`;

  // Во всех остальных случаях возвращаем исходную строку
  return phoneNumber;
}

// Типы ФРВ у которых разрешено изменение, включая сотрудников из других аптек (external)
export const frvTypeList = ['main', 'external'];

// Копирование текста в буфер обмена
export const copyText = async (value: string | number | null) => {
  try {
    if (value === null) {
      console.error("Ошибка копирования: value = null");
      return false;
    }

    await navigator.clipboard.writeText(value.toString());
  } catch (err) {
    console.error("Ошибка копирования: ", err);
  }
};